/**
 * STORE
 */

import { IAnchor } from '../types/anchor';
import { IEquatable } from '../types/entity';
import { ILocaleId, ILocalizable } from '../types/localized';
import { IMediaType } from '../types/media';
import { MenuCategoryStrategy } from '../types/menu';

export type IMockStore = {
  collections: {
    [key: string]: IMockCollection[];
  } & {
    category: IMockCategory[];
    label: IMockLabel[];
    locale: IMockLocale[];
    market: IMockMarket[];
    menu?: IMockMenu[];
    redirect?: IMockCollection[];
    template?: IMockTemplate[];
  },
  pages: Record<string, IMockPage[]>;
};

export type IMockPage = {
  id?: IEquatable;
  title: ILocalizable;
  abstract?: ILocalizable;
  description?: ILocalizable;
  media?: IMockMedia;
  meta?: IMockMeta;
  components?: IMockComponent[];
  category: string;
  slug?: ILocalizable;
  template?: string;
  isDefault?: boolean;
  isProtected?: boolean;
  useSplat?: boolean;
  order?: number;
  markets?: string[];
  $inherit?: boolean;
  [key: string]: unknown;
};

export type IMockComponent = {
  schema?: string;
  anchor?: IAnchor;
  [key: string]: unknown;
};

export type IMockMeta = {
  title?: ILocalizable;
  description?: ILocalizable;
  keywords?: ILocalizable;
  robots?: ILocalizable;
};

export type IMockTemplate = {
  id: string;
  name: string;
  collections?: string[];
};

export type IMockCategory = {
  id: string;
  title: ILocalizable;
  slug?: ILocalizable;
  category?: string;
  isHidden?: boolean;
};

export type IMockLocale = {
  id: ILocaleId;
  title: ILocalizable;
  isDefault?: boolean;
};

export type IMockMarket = {
  id: string;
  title: ILocalizable;
  isDefault?: boolean;
  isActive?: boolean;
  defaultLanguage?: ILocaleId;
  countries?: string[];
  languages?: ILocaleId[];
};

export type IMockLabel = {
  id: string;
  text: ILocalizable;
};

export type IMockMedia = {
  id?: IEquatable;
  title?: ILocalizable;
  abstract?: ILocalizable;
  alt?: ILocalizable;
  type: IMediaType;
  src: string;
  [key: string]: unknown;
};

// !!! check usage
export type IMockCollection = {
  id?: IEquatable;
  title?: ILocalizable;
  abstract?: ILocalizable;
  description?: ILocalizable;
  media?: IMockMedia;
  order?: number;
  markets?: string[];
  $inherit?: boolean;
  [key: string]: unknown;
};

export type IMockRedirect = {
  status: string;
  from: string;
  to?: string;
  name: string;
  order?: number;
  isActive?: boolean;
};

/**
 * MENU
 */
export type IMockMenu = {
  id: string;
  items: IMockMenuItem[];
};

export type IMockMenuItem = IMockMenuGroup | IMockMenuCategory | IMockMenuPage | IMockMenuLink | IMockMenuCustom;

export type IMockMenuGroup = IMockMenuBase & {
  type: 'group';
  title?: ILocalizable;
  items: IMockMenuItem[];
};

export type IMockMenuCategory = IMockMenuBase & IMockMenuExtra & {
  type: 'category';
  category: string;
  customTitle?: ILocalizable;
  strategy?: MenuCategoryStrategy;
  maxDepth?: number;
};

export type IMockMenuPage = IMockMenuBase & IMockMenuExtra & {
  type: 'page';
  page: IMockSingleRelation;
  customTitle?: ILocalizable;
};

export type IMockMenuLink = IMockMenuBase & {
  type: 'link';
  title: ILocalizable;
  href: string;
  target?: ('_self' | '_blank' | '_parent' | '_top' | '_unfencedTop') & string;
};

export type IMockMenuCustom = IMockMenuBase & {
  type: 'custom';
  title: ILocalizable;
  blockType: string;
};

/*
export type IMockMenuRoute = Omit<IMockMenuBase, 'type'> & {
  type: 'route';
  title: string;
  href: string;
};
*/

export type IMockMenuBase = {
  abstract?: ILocalizable;
  media?: IMockMedia;
  markets?: string[];
  customClass?: string;
};

export type IMockMenuExtra = {
  extra?: ILocalizable;
};

export type IMockRelation = {
  relationTo: string;
  value: IEquatable | IEquatable[];
};

export type IMockSingleRelation = {
  relationTo: string;
  value: IEquatable;
};

export type IMockManyRelation = {
  relationTo: string;
  value: IEquatable[];
};
